import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Plus, Trash2, CheckCircle2, Circle, AlertTriangle, Target, Trophy } from "lucide-react";

const LANES = [
  { key: "Focus", label: "Today's Focus", icon: Target, cls: "text-[#1E6FE8]" },
  { key: "Blocker", label: "Blockers", icon: AlertTriangle, cls: "text-red-600" },
  { key: "Win", label: "Wins", icon: Trophy, cls: "text-emerald-600" },
];
const today = () => new Date().toISOString().slice(0, 10);

export default function HuddleBoardPage() {
  const { projectId } = useParams();
  const [huddle, setHuddle] = useState([]);
  const [actions, setActions] = useState([]);
  const [issues, setIssues] = useState([]);
  const [text, setText] = useState("");
  const [lane, setLane] = useState("Focus");

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/artifacts/huddle_items`).then((r) => setHuddle(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/projects/${projectId}/artifacts/action_decision_log`).then((r) => setActions(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/artifacts/risk_issue_log`).then((r) => setIssues(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/projects/${projectId}/artifacts/huddle_items`, { text: text.trim(), lane, done: false, date: today() });
      setText(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (item) => {
    try { await api.put(`/projects/${projectId}/artifacts/huddle_items/${item.id}`, { ...item, done: !item.done }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (item) => {
    try { await api.delete(`/projects/${projectId}/artifacts/huddle_items/${item.id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openActions = useMemo(() => actions
    .filter((a) => (a.type || "A") === "A" && a.status !== "Closed")
    .sort((a, b) => String(a.dueDate || "9999").localeCompare(String(b.dueDate || "9999")))
    .slice(0, 8), [actions]);
  const openIssues = useMemo(() => issues.filter((i) => i.type === "Issue" && i.status !== "Closed").slice(0, 6), [issues]);
  const overdue = (d) => d && d < today();

  return (
    <div data-testid="huddle-board-page">
      <PageHeader overline={`Lean Toolkit · Daily stand-up board · ${today()}`} title="Huddle Board" />
      <div className="flex items-center gap-2 mt-2 mb-4">
        <div className="inline-flex border border-border">
          {LANES.map((l) => (
            <button key={l.key} data-testid={`huddle-lane-${l.key.toLowerCase()}`} onClick={() => setLane(l.key)}
              className={`px-3 py-1.5 text-xs font-semibold ${lane === l.key ? "bg-[#1E6FE8] text-white" : "text-muted-foreground hover:text-foreground"}`}>{l.key}</button>
          ))}
        </div>
        <Input data-testid="huddle-text-input" className="max-w-md h-9" placeholder="Add a focus item, blocker or win…"
          value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button data-testid="huddle-add-btn" className="rounded-none h-9" onClick={add} disabled={!text.trim()}><Plus className="h-4 w-4" /></Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {LANES.map((l) => {
          const list = huddle.filter((h) => h.lane === l.key).sort((a, b) => Number(a.done) - Number(b.done));
          return (
            <div key={l.key} data-testid={`huddle-column-${l.key.toLowerCase()}`} className="border border-border bg-card">
              <div className={`flex items-center gap-2 px-3 py-2 border-b border-border font-display font-bold text-sm tracking-tight ${l.cls}`}>
                <l.icon className="h-4 w-4" /> {l.label} <span className="text-muted-foreground font-normal">({list.filter((x) => !x.done).length})</span>
              </div>
              <div className="p-2 space-y-1.5 min-h-[120px]">
                {list.length === 0 && <div className="text-xs text-muted-foreground p-2">Nothing here yet.</div>}
                {list.map((item) => (
                  <div key={item.id} data-testid="huddle-item" className={`flex items-start gap-2 border border-border px-2.5 py-2 text-sm ${item.done ? "opacity-50" : ""}`}>
                    <button data-testid="huddle-toggle-btn" onClick={() => toggle(item)} className="mt-0.5 shrink-0">
                      {item.done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                    </button>
                    <span className={`flex-1 ${item.done ? "line-through" : ""}`}>{item.text}</span>
                    <span className="text-[10px] text-muted-foreground shrink-0">{item.date}</span>
                    <button data-testid="huddle-delete-btn" onClick={() => remove(item)} className="text-muted-foreground hover:text-red-600 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="border border-border bg-card" data-testid="huddle-open-actions">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="font-display font-bold text-sm tracking-tight">Open actions (next due)</span>
            <Link to={`/projects/${projectId}/action_decision_log`} className="text-xs text-[#1E6FE8] underline">Actions & Decisions →</Link>
          </div>
          <div className="divide-y divide-border">
            {openActions.length === 0 && <div className="text-xs text-muted-foreground p-3">No open actions.</div>}
            {openActions.map((a) => (
              <div key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="flex-1 truncate">{a.actionOrDecision}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">{a.owner}</span>
                <span className={`text-xs whitespace-nowrap ${overdue(a.dueDate) ? "text-red-600 font-semibold" : "text-muted-foreground"}`}>{a.dueDate || "—"}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border bg-card" data-testid="huddle-open-issues">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <span className="font-display font-bold text-sm tracking-tight">Open issues</span>
            <Link to={`/projects/${projectId}/risk_issue_log`} className="text-xs text-[#1E6FE8] underline">Risk & Issue Log →</Link>
          </div>
          <div className="divide-y divide-border">
            {openIssues.length === 0 && <div className="text-xs text-muted-foreground p-3">No open issues.</div>}
            {openIssues.map((i) => (
              <div key={i.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span className="flex-1 truncate">{i.description}</span>
                <StatusBadge value={i.impact} />
                <span className="text-xs text-muted-foreground whitespace-nowrap">{i.owner}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
